import React, { useEffect, useState, type ReactNode } from 'react';
import {
  ThemeContext,
  type Theme,
  type ThemePreference,
} from './ThemeContext';

interface Props {
  children: ReactNode;
}

const STORAGE_KEY = 'theme';

const isPreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

export const ThemeProvider: React.FC<Props> = ({ children }) => {
  const [preference, setPreference] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return 'system';
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isPreference(saved)) return saved;
    return 'system';
  });

  const [systemDark, setSystemDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Resolve the OS preference whenever the user is on `system`. The media-query
  // listener only matters while `preference === 'system'` — for explicit
  // light/dark we don't need to react to OS changes.
  useEffect(() => {
    if (preference !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener('change', handler);
    setSystemDark(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [preference]);

  const theme: Theme =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  const toggleTheme = () => {
    setPreference((prev) => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'light';
      return systemDark ? 'light' : 'dark';
    });
  };

  useEffect(() => {
    const root = document.documentElement;

    root.style.visibility = 'hidden';

    requestAnimationFrame(() => {
      if (theme === 'dark') {
        root.classList.add('dark');
        root.style.colorScheme = 'dark';
      } else {
        root.classList.remove('dark');
        root.style.colorScheme = 'light';
      }

      root.getBoundingClientRect();

      root.style.visibility = '';
    });

    localStorage.setItem(STORAGE_KEY, preference);
  }, [theme, preference]);

  return (
    <ThemeContext.Provider value={{ theme, preference, setPreference, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
